/**
 * Icon — transcribed from `Oprappo Design System.dc.html § 01 ভিত্তি · আইকন`
 * and the icons drawn inside the § 02–§ 07 components.
 *
 * The rule from § 01: "24px grid, 2px stroke, square caps on structure and round on
 * wires, single weight, no fills, no colour except state." Every path below is copied
 * verbatim from the design; nothing is redrawn.
 *
 * Six of the nine icon-only controls (bell, cross, three dots, send, avatar, lock) are
 * NOT drawn in the design system file. Their paths are transcribed from the screens
 * where they do appear — see README.md § "Icons sourced outside the design system".
 */
import type { SVGProps } from 'react';

export type IconName =
  // § 01 iconography row — the five category icons
  | 'recharge'
  | 'bill'
  | 'market'
  | 'transport'
  | 'business'
  // § 02 search
  | 'search'
  | 'voice'
  | 'image'
  // § 02 select / date / evidence — chevrons
  | 'chevronDown'
  | 'chevronUp'
  | 'chevronLeft'
  | 'chevronRight'
  // § 02 upload
  | 'upload'
  | 'file'
  // § 03 bottom tab bar
  | 'home'
  | 'explore'
  | 'ai'
  | 'activity'
  | 'account'
  // icon-only controls sourced from the P0 screens
  | 'bell'
  | 'close'
  | 'more'
  | 'send'
  | 'lock';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children' | 'width' | 'height'> {
  name: IconName;
  /**
   * Rendered size in px. Sizes are per-usage in the design (19 in the search bar,
   * 22 in the tab bar, 26 in the § 01 grid, 16 for chevrons); the caller passes the
   * value from the frame it is building.
   */
  size: number;
  /**
   * Stroke colour. § 01: "no colour except state". Defaults to `#0E3A45` (ink-teal),
   * which is the colour every § 01 icon is drawn in.
   */
  stroke?: string | undefined;
}

const SW = 2;

function paths(name: IconName, stroke: string): JSX.Element {
  switch (name) {
    // ── § 01 আইকন ─────────────────────────────────────────────────────────────
    case 'recharge': // রিচার্জ
      return (
        <>
          <rect x="7" y="2.5" width="10" height="19" rx="1.5" stroke={stroke} strokeWidth={SW} />
          <path d="M10 18.5h4" stroke={stroke} strokeWidth={SW} />
        </>
      );
    case 'bill': // বিল
      return (
        <>
          <rect x="3" y="4" width="18" height="16" rx="1.5" stroke={stroke} strokeWidth={SW} />
          <path d="M3 9h18" stroke={stroke} strokeWidth={SW} />
        </>
      );
    case 'market': // বাজার
      return (
        <>
          <path d="M3.5 7h17l-1.5 13h-14z" stroke={stroke} strokeWidth={SW} strokeLinejoin="round" />
          <path d="M9 7V5a3 3 0 016 0v2" stroke={stroke} strokeWidth={SW} />
        </>
      );
    case 'transport': // যাতায়াত
      return (
        <>
          <path d="M3 15h18M6 15V9l3-3h6l3 3v6" stroke={stroke} strokeWidth={SW} strokeLinejoin="round" />
          <circle cx="8" cy="18" r="1.6" stroke={stroke} strokeWidth={SW} />
          <circle cx="16" cy="18" r="1.6" stroke={stroke} strokeWidth={SW} />
        </>
      );
    case 'business': // ব্যবসা
      return (
        <>
          <path d="M4 20V9l8-5 8 5v11" stroke={stroke} strokeWidth={SW} strokeLinejoin="round" />
          <path d="M10 20v-6h4v6" stroke={stroke} strokeWidth={SW} />
        </>
      );

    // ── § 02 সার্চ ────────────────────────────────────────────────────────────
    case 'search':
      return (
        <>
          <circle cx="11" cy="11" r="7" stroke={stroke} strokeWidth={SW} />
          <path d="M16 16l4 4" stroke={stroke} strokeWidth={SW} />
        </>
      );
    case 'voice':
      return (
        <>
          <rect x="9" y="3" width="6" height="11" rx="3" stroke={stroke} strokeWidth={SW} />
          <path d="M5 11a7 7 0 0014 0M12 18v3" stroke={stroke} strokeWidth={SW} />
        </>
      );
    case 'image':
      return (
        <>
          <rect x="3" y="5" width="18" height="14" rx="1.5" stroke={stroke} strokeWidth={SW} />
          <circle cx="9" cy="10" r="1.8" stroke={stroke} strokeWidth={SW} />
          <path d="M4 17l5-4 4 3 3-2 4 3" stroke={stroke} strokeWidth={SW} strokeLinejoin="round" />
        </>
      );

    // ── chevrons ─────────────────────────────────────────────────────────────
    case 'chevronDown':
      return (
        <path d="M6 9l6 6 6-6" stroke={stroke} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round" />
      );
    case 'chevronUp':
      return (
        <path d="M6 15l6-6 6 6" stroke={stroke} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round" />
      );
    case 'chevronLeft':
      return (
        <path d="M14 6l-6 6 6 6" stroke={stroke} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round" />
      );
    case 'chevronRight':
      return (
        <path d="M10 6l6 6-6 6" stroke={stroke} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round" />
      );

    // ── § 02 আপলোড ───────────────────────────────────────────────────────────
    case 'upload':
      return (
        <>
          <path d="M12 16V4m0 0L7 9m5-5l5 5" stroke={stroke} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round" />
          <path d="M4 18v2h16v-2" stroke={stroke} strokeWidth={SW} strokeLinecap="round" />
        </>
      );
    case 'file':
      return (
        <>
          <path d="M6 2.5h8l4 4v15H6z" stroke={stroke} strokeWidth={SW} strokeLinejoin="round" />
          <path d="M14 2.5v4h4" stroke={stroke} strokeWidth={SW} strokeLinejoin="round" />
        </>
      );

    // ── § 03 নেভিগেশন — bottom tab bar ───────────────────────────────────────
    case 'home':
      return <path d="M4 20V9l8-5 8 5v11" stroke={stroke} strokeWidth={SW} strokeLinejoin="round" />;
    case 'explore':
      return (
        <>
          <rect x="3.5" y="3.5" width="7" height="7" stroke={stroke} strokeWidth={SW} />
          <rect x="13.5" y="3.5" width="7" height="7" stroke={stroke} strokeWidth={SW} />
          <rect x="3.5" y="13.5" width="7" height="7" stroke={stroke} strokeWidth={SW} />
          <rect x="13.5" y="13.5" width="7" height="7" stroke={stroke} strokeWidth={SW} />
        </>
      );
    case 'ai':
      return (
        <path
          d="M12 3l2.2 6.2L20.5 11l-6.3 1.8L12 19l-2.2-6.2L3.5 11l6.3-1.8z"
          stroke={stroke}
          strokeWidth={SW}
          strokeLinejoin="round"
        />
      );
    case 'activity':
      return <path d="M4 6h16M4 12h16M4 18h10" stroke={stroke} strokeWidth={SW} strokeLinecap="round" />;
    case 'account':
      return (
        <>
          <circle cx="12" cy="8" r="4" stroke={stroke} strokeWidth={SW} />
          <path
            d="M4.5 21c0-4.1 3.4-6.5 7.5-6.5s7.5 2.4 7.5 6.5"
            stroke={stroke}
            strokeWidth={SW}
            strokeLinecap="round"
          />
        </>
      );

    // ── icon-only controls sourced from the P0 screens ───────────────────────
    case 'bell':
      return (
        <>
          <path
            d="M12 3a6 6 0 016 6v4l2 3H4l2-3V9a6 6 0 016-6z"
            stroke={stroke}
            strokeWidth={SW}
            strokeLinejoin="round"
          />
          <path d="M10 19a2 2 0 004 0" stroke={stroke} strokeWidth={SW} />
        </>
      );
    case 'close':
      return <path d="M6 6l12 12M18 6L6 18" stroke={stroke} strokeWidth={SW} strokeLinecap="round" />;
    case 'more':
      return (
        <>
          <circle cx="12" cy="5" r="1.6" fill={stroke} />
          <circle cx="12" cy="12" r="1.6" fill={stroke} />
          <circle cx="12" cy="19" r="1.6" fill={stroke} />
        </>
      );
    case 'send':
      return (
        <path
          d="M4 12h14M13 7l5 5-5 5"
          stroke={stroke}
          strokeWidth={SW}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'lock':
      return (
        <>
          <rect x="5" y="10" width="14" height="10" rx="1.5" stroke={stroke} strokeWidth={SW} />
          <path d="M8.5 10V7a3.5 3.5 0 017 0v3" stroke={stroke} strokeWidth={SW} />
        </>
      );
  }
}

export function Icon({ name, size, stroke = '#0E3A45', ...rest }: IconProps): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden {...rest}>
      {paths(name, stroke)}
    </svg>
  );
}

/**
 * IconTile — § 01 আইকন grid cell. 56px wide, centred, 26px icon, Bangla label
 * `500 13px/1.75 'Anek Bangla'` in ink, 4px below the glyph.
 *
 * § 01: "Every icon carries a Bangla label — an icon-only grid is unusable for a
 * first-time user, and this market has many."
 */
export interface IconTileProps {
  name: IconName;
  /** Bangla label. Never omit it — an unlabelled tile is not in the system. */
  labelBn: string;
  onClick?: (() => void) | undefined;
}

export function IconTile({ name, labelBn, onClick }: IconTileProps): JSX.Element {
  return (
    <button type="button" onClick={onClick} className="w-[56px] text-center bg-transparent border-0 p-0">
      <Icon name={name} size={26} />
      <div className="font-anek font-medium text-[13px] leading-[1.75] text-ink mt-[4px]">{labelBn}</div>
    </button>
  );
}
